import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Tag } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import TableHeaderDropdownSearch from '@/components/data-table/TableHeaderDropdownSearch';
import TableHeaderSearch from '@/components/data-table/TableHeaderSearch';
import TableHeaderSort from '@/components/data-table/TableHeaderSort';
import TableHeaderTitle from '@/components/data-table/TableHeaderTitle';
import TruncateTooltipCell from '@/components/data-table/TruncateTooltipCell';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { API } from '@/services/api.service';
import { FINANCIAL_STATEMENTS_DEFAULT_PARAMS } from '@/shared/financialStatements';
import StatementsActions from './StatementsActions';

const STATUS_COLORS = {
  DRAFT: 'default',
  GENERATED: 'processing',
  SUBMITTED: 'success',
};

const COMPANY_SIZE_COLORS = {
  MIKRO: 'cyan',
  MALO: 'orange',
  SREDNJE: 'gold',
  VELIKO: 'magenta',
};

const STATUS_ICONS = {
  DRAFT: <ClockCircleOutlined />,
  GENERATED: <FileTextOutlined />,
  SUBMITTED: <CheckCircleOutlined />,
};

export const StatementsColumns = () => {
  const { t, i18n } = useTranslation(['tables', 'forms', 'menus']);
  const fs = (key) => t(`financialStatements.${key}`, { ns: 'tables' });

  const { queryParams, setQueryParams } = useQueryParamsContext();

  // Helper for localized labels
  const getLocalizedLabel = (item) => {
    const lang = i18n.language;
    if (lang === 'sr') return item.labelSr || item.label;
    if (lang === 'ru') return item.labelRu || item.label;
    return item.labelEn || item.label;
  };

  // Fetch company sizes enum
  const { data: companySizesData } = useQuery({
    queryKey: ['financial-statements-company-sizes'],
    queryFn: () => API.financialStatements.getCompanySizesEnum(),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const companySizes = companySizesData?.data || [];

  // Fetch statuses enum
  const { data: statusesData } = useQuery({
    queryKey: ['financial-statements-statuses'],
    queryFn: () => API.financialStatements.getStatusesEnum(),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const statuses = statusesData?.data || [];

  // Year options (last 5 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => ({
    label: String(currentYear - i),
    value: String(currentYear - i),
  }));

  // Company size options
  const companySizeOptions = companySizes.map((size) => ({
    label: getLocalizedLabel(size),
    value: size.key,
  }));

  // Status options
  const statusOptions = statuses.map((status) => ({
    label: getLocalizedLabel(status),
    value: status.key,
  }));

  const [toggleDropdown, setToggleDropdown] = useState({
    companyName: false,
    year: false,
    companySize: false,
    status: false,
  });

  const [searchTerm, setSearchTerm] = useState(FINANCIAL_STATEMENTS_DEFAULT_PARAMS);

  useEffect(() => {
    const years = queryParams?.year
      ? Array.isArray(queryParams.year)
        ? queryParams.year
        : [queryParams.year]
      : undefined;

    const sizes = queryParams?.companySize
      ? Array.isArray(queryParams.companySize)
        ? queryParams.companySize
        : [queryParams.companySize]
      : undefined;

    const statusList = queryParams?.status
      ? Array.isArray(queryParams.status)
        ? queryParams.status
        : [queryParams.status]
      : undefined;

    setSearchTerm({
      companyName: queryParams?.companyName || '',
      year: years,
      companySize: sizes,
      status: statusList,
    });
  }, [queryParams]);

  const handleClose = (name, value) => {
    setToggleDropdown((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleSearchTerm = (name, value) => {
    setSearchTerm((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  // For text search (company name)
  const handleSubmit = (name, form) => {
    const value = form[name];

    handleClose(name, false);

    if (value && value?.length > 0) {
      setQueryParams({ [name]: value, offset: 1 });
    } else {
      setQueryParams({ [name]: undefined });
    }
  };

  // For text search reset
  const handleSearchReset = (name) => {
    setQueryParams({ [name]: undefined });

    setSearchTerm((prev) => {
      return {
        ...prev,
        [name]: '',
      };
    });

    handleClose(name, false);
  };

  // For checkbox dropdown reset
  const handleCheckboxReset = (name) => {
    setQueryParams({ [name]: undefined, offset: 1 });

    setSearchTerm((prev) => {
      return {
        ...prev,
        [name]: [],
      };
    });

    handleClose(name, false);
  };

  return [
    {
      title: () => {
        return (
          <TableHeaderTitle title={t('company', { ns: 'tables' })}>
            <TableHeaderSearch
              dataIndex="companyName"
              title={t('company', { ns: 'tables' })}
              searchTerm={searchTerm}
              toggleDropdown={toggleDropdown}
              onChange={handleSearchTerm}
              onReset={handleSearchReset}
              onClose={handleClose}
              onSubmit={handleSubmit}
            />
            <TableHeaderSort dataIndex="companyName" />
          </TableHeaderTitle>
        );
      },
      dataIndex: 'company',
      key: 'company',
      width: 180,
      render: (_, record) => {
        const name = record.company?.name || record.companyName || '-';
        return (
          <TruncateTooltipCell label={name} maxLength={25} />
        );
      },
    },
    {
      title: () => {
        return (
          <TableHeaderTitle title={fs('year')}>
            <TableHeaderDropdownSearch
              dataIndex="year"
              title={fs('year')}
              searchTerm={searchTerm}
              toggleDropdown={toggleDropdown}
              options={yearOptions}
              onChange={handleSearchTerm}
              onReset={handleCheckboxReset}
              onClose={handleClose}
              onSubmit={handleSubmit}
              dropdownWidth={120}
              placement="bottom"
            />
            <TableHeaderSort dataIndex="year" />
          </TableHeaderTitle>
        );
      },
      dataIndex: 'year',
      key: 'year',
      width: 100,
    },
    {
      title: () => {
        return (
          <TableHeaderTitle title={fs('companySize')}>
            <TableHeaderDropdownSearch
              dataIndex="companySize"
              title={fs('companySize')}
              searchTerm={searchTerm}
              toggleDropdown={toggleDropdown}
              options={companySizeOptions}
              onChange={handleSearchTerm}
              onReset={handleCheckboxReset}
              onClose={handleClose}
              onSubmit={handleSubmit}
              dropdownWidth={150}
              placement="bottom"
            />
          </TableHeaderTitle>
        );
      },
      dataIndex: 'companySize',
      key: 'companySize',
      width: 120,
      render: (size) => {
        const sizeObj = companySizes.find((s) => s.key === size);
        return (
          <Tag color={COMPANY_SIZE_COLORS[size] || 'default'}>
            {sizeObj ? getLocalizedLabel(sizeObj) : size}
          </Tag>
        );
      },
    },
    {
      title: () => {
        return (
          <TableHeaderTitle title={t('status', { ns: 'tables' })}>
            <TableHeaderDropdownSearch
              dataIndex="status"
              title={t('status', { ns: 'tables' })}
              searchTerm={searchTerm}
              toggleDropdown={toggleDropdown}
              options={statusOptions}
              onChange={handleSearchTerm}
              onReset={handleCheckboxReset}
              onClose={handleClose}
              onSubmit={handleSubmit}
              dropdownWidth={150}
              placement="bottom"
            />
          </TableHeaderTitle>
        );
      },
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status) => {
        const statusObj = statuses.find((s) => s.key === status);
        return (
          <Tag color={STATUS_COLORS[status]} icon={STATUS_ICONS[status]}>
            {statusObj ? getLocalizedLabel(statusObj) : status}
          </Tag>
        );
      },
    },
    {
      title: () => {
        return (
          <TableHeaderTitle title={fs('generatedAt')}>
            <TableHeaderSort dataIndex="generatedAt" />
          </TableHeaderTitle>
        );
      },
      dataIndex: 'generatedAt',
      key: 'generatedAt',
      width: 150,
      render: (date) => (date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '-'),
    },
    {
      title: () => {
        return (
          <TableHeaderTitle title={fs('generatedBy')}>
            <TableHeaderSort dataIndex="generatedBy" />
          </TableHeaderTitle>
        );
      },
      dataIndex: 'generatedBy',
      key: 'generatedBy',
      width: 180,
      render: (_, record) =>
        record.generatedBy ? (
          <div>
            <div className="text-sm">
              {record.generatedBy.firstName} {record.generatedBy.lastName}
            </div>
            <div className="text-xs text-gray-500">{record.generatedBy.email}</div>
          </div>
        ) : (
          '-'
        ),
    },
    {
      title: fs('xmlFiles'),
      key: 'xmlFiles',
      width: 150,
      render: (_, record) => <StatementsActions record={record} type="download" />,
    },
    {
      title: t('actions', { ns: 'tables' }),
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => <StatementsActions record={record} type="actions" />,
    },
  ];
};
